import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Center,
  createStyles,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthletePackage from 'hooks/athletePackage/useAthletePackage';
import { useNavigate } from 'react-router-dom';
import { AthletePackageList, CreditDispensation } from 'types';
import { formatAthletePackageValidityDisplay } from 'utils/athlete-package.helpers';

import CreditDispensationRow from './CreditDispensationRow';

const useStyles = createStyles({
  badgeContainer: {
    position: 'absolute',
    top: -10,
    right: 10,
  },
});

const AthletePackageCard = ({
  athletePackage,
}: {
  athletePackage: AthletePackageList;
}): JSX.Element | null => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();
  const { session } = useAuth();

  const [showAllCredits, setShowAllCredits] = useState<boolean>(false);
  const [showToggle, setShowToggle] = useState<boolean>(true);
  const [creditDispensationsDisplayed, setCreditDispensationsDisplayed] = useState<CreditDispensation[]>([]);

  const navigate = useNavigate();

  const { data, isLoading, isSuccess, isError } = useAthletePackage(athletePackage.athletePackageId);

  useEffect(() => {
    // If show all is true display all credits
    // If show all is false display active credits if possible, otherwise show first credit
    if (isSuccess && data?.data) {
      const today = dayjs(new Date()).format('YYYY-MM-DD');
      const sorted = data.data.creditDispensations.sort((a, b) =>
        a.dispenseDate < b.dispenseDate ? -1 : 1,
      );
      // Handle when no credits exist
      if (sorted.length === 0) {
        setShowToggle(false);
        setCreditDispensationsDisplayed([]);
      } else if (showAllCredits) {
        setCreditDispensationsDisplayed(sorted);
      } else {
        let relevantCredits = sorted.filter(
          creditDispensation =>
            creditDispensation.dispenseDate <= today && today <= (creditDispensation.expirationDate ?? ''),
        );
        if (relevantCredits.length === 0) {
          setCreditDispensationsDisplayed([sorted[0]]);
        } else {
          if (sorted.length === relevantCredits.length) {
            // All credits are active so toggle will do nothing
            setShowToggle(false);
          }
          setCreditDispensationsDisplayed(relevantCredits);
        }
      }
    }
  }, [data?.data, isSuccess, showAllCredits]);

  return (
    <Paper shadow='xs' radius='lg' p='sm' mb='sm' pos='relative'>
      <LoadingOverlay radius='lg' visible={isLoading} />

      <div className={classes.badgeContainer}>
        <PackageStatusBadge
          status={athletePackage.status}
          isCancelEarly={athletePackage.cancellationDate !== null}
          size='md'
          w='min-content'
        />
      </div>

      <Group position='apart' noWrap>
        <Title
          order={4}
          underline
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/packages/${athletePackage.athletePackageId}`)}
        >
          {athletePackage.package.name}
        </Title>
        <Group noWrap spacing='xs' pr='lg'>
          {formatAthletePackageValidityDisplay(athletePackage, dateFormat, ' - ', session?.gym?.timezone)}
        </Group>
      </Group>

      {athletePackage.cancellationDate && (
        <Text size='sm' color='dimmed' fs='italic' align='right' pr='lg'>
          Early Cancellation
        </Text>
      )}

      <Divider mx='-sm' my='xs' />

      {isError && (
        <Center sx={{ flexGrow: 1 }} p='lg'>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong while fetching data!
          </Alert>
        </Center>
      )}

      {isSuccess && data.data && (
        <Stack>
          {creditDispensationsDisplayed.length === 0 ? (
            <Text size='sm' color='dimmed' fw={600}>
              No active credits
            </Text>
          ) : (
            creditDispensationsDisplayed.map(creditDispensation => (
              <CreditDispensationRow
                key={creditDispensation.creditDispensationId}
                creditDispensation={creditDispensation}
              />
            ))
          )}

          {data.data.creditDispensations.length > 1 && showToggle && (
            <Button
              compact
              variant='subtle'
              disabled={isLoading}
              onClick={() => setShowAllCredits(!showAllCredits)}
            >
              {showAllCredits ? 'Show less' : 'Show more'}
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default AthletePackageCard;
